import { cn } from '@/lib/utils'
import { memo, type ReactNode } from 'react'

// The list is ranked most-like-you (top) → least-like-you (bottom). Convey that
// as a colored scale rather than a neutral ordinal: a green (primary) plus for
// "most like me", a neutral grey midpoint with a small dot, and a red minus for
// "least like me".
const rankBgClasses = ['bg-primary/25', 'bg-muted', 'bg-destructive/15']

// Shared flex-row class for a rank option. The CALLER puts this on its own element (the <li> in the
// list, a <div> in the DragOverlay) so the row needs no wrapper of its own — one fewer node per option.
// `text-sm leading-snug` lives here (not a text wrapper) so the statement can render as a bare text node.
export const RANK_ROW_CLASS =
  'bg-card border-border flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 text-sm leading-snug'

// The drag grip drawn with a SINGLE node: six hard-stop radial-gradient dots placed at explicit
// pixel positions (2 columns × 3 rows), reproducing the lucide grip-vertical it replaces. Explicit
// positions + a hard color stop keep the dots crisp and evenly spaced (tiling a single gradient
// rounded sub-pixels and looked smeared/uneven).
const GRIP_DOTS = [1, 5]
  .flatMap((x) =>
    [2.5, 7, 11.5].map((y) => `radial-gradient(circle 1px at ${x}px ${y}px,currentColor 99%,transparent 100%)`),
  )
  .join(',')

// Memoized: rendered up to 4 times per drag tick (3 in the active list + 1 in the DragOverlay). All
// call sites pass referentially-stable `icon` elements and `text` strings, so memo skips re-render for
// items whose rankPosition didn't change.
//
// Returns the row's CONTENTS as a fragment (no wrapper element) so the parent <li>/<div> IS the row —
// keeping the per-question node count low (every question's 3 items stay mounted in the carousel).
// Flat by design: a CSS-dot grip instead of a 6-circle svg, and only the ONE active rank glyph (the
// inactive Plus/Minus isn't kept around for a crossfade).
export const TestRankItem = memo(function TestRankItem(props: {
  rankPosition: number
  icon: ReactNode
  text: string
  grabbed?: boolean
}) {
  const showUp = props.rankPosition === 0
  const showDown = props.rankPosition === 2
  const bgClass = rankBgClasses[props.rankPosition] ?? rankBgClasses[1]
  const durationClass = props.grabbed ? 'duration-[350ms]' : 'duration-[120ms]'

  return (
    <>
      <span
        aria-hidden
        className="text-muted-foreground/40 h-[14px] w-[6px] shrink-0 bg-no-repeat"
        style={{ backgroundImage: GRIP_DOTS }}
      />
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className={cn(
          'size-6 shrink-0 rounded-full p-1 transition-colors ease-out',
          durationClass,
          bgClass,
          showUp && 'text-primary',
          showDown && 'text-destructive',
        )}
      >
        {showUp && <path d="M5 12h14M12 5v14" />}
        {showDown && <path d="M5 12h14" />}
      </svg>
      {props.text}
      {props.icon}
    </>
  )
})
